import React, { useEffect, useState, useContext, useMemo } from "react";
import styles from "./Notes.module.scss";
import { useMutation, useQuery } from "@apollo/client";
import gql from "graphql-tag";
import _ from "lodash";
import { DialogContext } from "../../context/dialogContext";
import { FetchContext } from "../../context/fetchContext";
import { SnackbarContext } from "../../context/snackbarContext";
import { NotesContext } from "../../context/notesContext";
import { useAuth } from "../../context/authContext";
import { useUser } from "../../context/userContext";
import { useParams } from "react-router-dom";
import useNotes from "../../components/Notes/useNotes";
import useMobileMenu from "../../hooks/useMobileMenu";
import { isAuthed } from "../../utils/authorization";
import { addToDate, formatAs, getCurrentQuarter, quarterDates } from "../../utils/dates";
import { GET_NOTES } from "../../utils/query";
import { useDepartmentFilter } from "../../context/departmentFilterContext";
import useCorpPlan from "../../hooks/useCorpPlan";
import useDidUpdateEffect from "../../hooks/useDidUpdateEffect";

import {
  Container,
  Card,
  CardContent,
  CardActions,
  Typography,
  Grid,
  Button,
  TextField,
  TablePagination,
  IconButton,
  useTheme,
} from "@material-ui/core";
import Icon from "@mdi/react";
import { mdiPlus, mdiCheckboxMarked, mdiAlertDecagram, mdiNoteText } from "@mdi/js";

import CardTitle from "../../components/CardTitle/CardTitle";
import NotesList from "../../components/Notes/NotesList";
import Loading from "../../components/Loading/Loading";
import AddEditNoteDialog from "../../components/AddEditNoteDialog/AddEditNoteDialog";
import SelectCorpYear from "../../components/SelectCorpYear/SelectCorpYear";

const Notes = ({ params, org }) => {
  const theme = useTheme();

  const { auth } = useAuth();
  const { isMobile, renderMobileMenu } = useMobileMenu();
  const { user: loggedInUser } = useUser();

  const { id: orgId, fiscalYear } = org;
  const quarter = getCurrentQuarter(fiscalYear);
  const [start, end] = quarterDates(addToDate(fiscalYear, { days: 1 }), quarter);
  const currentQuarter = { quarter, start, end };

  const { corpForSelectedYear, setCorpForSelectedYear, corpPlans } = useCorpPlan({ orgId, fiscalYear: org.fiscalYear });
  const currCorpPlanId = _.get(corpForSelectedYear, "id");

  const { departmentFilter } = useDepartmentFilter();
  const deptFilterSpid = _.get(departmentFilter, "sharedPlanId");

  const { requestFetch, fetch } = useContext(FetchContext);
  const { snack } = useContext(SnackbarContext);

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const [currPageIdx, setCurrPageIdx] = useState(0);
  const [notesPerPage, setNotesPerPage] = useState(10);

  const offset = currPageIdx * notesPerPage;

  // Query for notes with pagination
  const { loading, error, data, refetch, fetchMore } = useQuery(GET_NOTES, {
    variables: {
      offset: offset,
      limit: notesPerPage,
      organization: orgId,
      corpPlan: currCorpPlanId,
      sharedPlanId: deptFilterSpid,
      pinnedFirst: true,
      search: debouncedSearch || undefined,
    },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: "network-only",
  });

  const {
    targetNote,
    addEditOpen,
    handleAddEdit,
    handleClose,
    handleChangeFile,
    handleChangeText,
    handleCreate,
    handleUpdate,
    handleDelete,
    handleRemoveImage,
    handleCopyImageToClipboard,
    handleOpenFileInNewTab,
    handlePin,
  } = useNotes({
    user: auth,
    snack,
  });

  // Handler for changing pages
  const handlePageChange = (newPageIdx) => {
    setCurrPageIdx(newPageIdx);

    fetchMore({
      variables: {
        offset: newPageIdx * notesPerPage,
      },
    });
  };

  const handleAddDialog = () => {
    handleAddEdit(true);
  };

  useEffect(() => {
    refetch();
  }, [fetch]);

  // Handle search debouncing
  useDidUpdateEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      // Reset to first page when search changes
      setCurrPageIdx(0);
    }, 300);

    return () => clearTimeout(timer);
  }, [search]);

  useDidUpdateEffect(() => {
    // Reset to first page when amount notes displayed per page changes
    setCurrPageIdx(0);
  }, [notesPerPage]);

  const notesData = _.get(data, "notes", {});
  const notes = _.get(notesData, "notes", []);
  const pageInfo = _.get(notesData, "pageInfo", {});

  if (loading) return <Loading />;

  return (
    <>
      <Container maxWidth={false}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <div className={styles.flexBetween}>
              <div>
                {currentQuarter && (
                  <Grid item xs={12}>
                    <Typography variant="h5">
                      <span className={styles.label}>Current Quarter: </span>
                      {currentQuarter.quarter} ({formatAs(currentQuarter.start, "MMM d")} - {formatAs(currentQuarter.end, "MMM d")})
                    </Typography>
                    <SelectCorpYear
                      corporatePlans={corpPlans}
                      fiscalYear={fiscalYear}
                      corpForSelectedYear={corpForSelectedYear}
                      setCorpForSelectedYear={setCorpForSelectedYear}
                    />
                  </Grid>
                )}
              </div>
            </div>
          </Grid>
          <Grid item xs={12}>
            <Card className={styles.card}>
              <CardTitle vertical color="indigo">
                <Icon path={mdiNoteText} size={1} color="#fff" className={styles.icon} />
                <Typography variant="h5" className={styles.cardTitle}>
                  Notes
                </Typography>
              </CardTitle>
              <CardActions className={styles.cardActions}>
                {renderMobileMenu(
                  <TextField
                    className={styles.searchField}
                    label="Search"
                    type="search"
                    variant="outlined"
                    size="small"
                    value={search || ""}
                    onChange={(event) => {
                      if (_.isFunction(setSearch)) setSearch(event.target.value);
                    }}
                  />
                )}
                {isMobile ? (
                  <IconButton
                    onClick={handleAddDialog}
                    // disabled={!canEdit}
                    className={styles.menuButton}
                  >
                    <Icon path={mdiPlus} size={0.75} color="#fff" />
                  </IconButton>
                ) : (
                  <Button
                    startIcon={<Icon path={mdiPlus} size={1} color="#fff" />}
                    className={styles.menuButton}
                    onClick={handleAddDialog}
                    variant="contained"
                    color="primary"
                    // disabled={!canEdit}
                  >
                    New Note
                  </Button>
                )}
              </CardActions>
              <CardContent className={styles.cardContent} style={{ paddingLeft: theme.spacing(8) }}>
                <NotesList
                  notes={notes}
                  handleOpenFile={handleOpenFileInNewTab}
                  toolsProps={{
                    handlePin,
                    handleDelete,
                    handleEdit: (note) => handleAddEdit(true, note),
                    handleRemoveImage,
                    handleCopyImage: handleCopyImageToClipboard,
                  }}
                />
                <TablePagination
                  component={"div"}
                  count={_.get(pageInfo, "totalCount")}
                  rowsPerPage={notesPerPage}
                  page={currPageIdx}
                  onPageChange={(e, page) => handlePageChange(page)}
                  onRowsPerPageChange={(e) => setNotesPerPage(e.target.value)}
                  rowsPerPageOptions={[10, 25, 50, 100, 200]}
                  style={{ marginTop: "auto" }}
                />
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
      <AddEditNoteDialog
        open={addEditOpen}
        note={targetNote}
        orgId={orgId}
        currCorpPlanId={currCorpPlanId}
        deptFilterSpid={deptFilterSpid}
        handleCreate={handleCreate}
        handleUpdate={handleUpdate}
        handleChangeFile={handleChangeFile}
        handleChangeText={handleChangeText}
        handleClose={handleClose}
      />
    </>
  );
};

export default Notes;
